import React, { useEffect, useState } from "react";
import { Functions } from "appwrite";
import { ClipLoader } from "react-spinners";
import client from "./appwriteConfig";
import { useUser } from "./UserContext";
import "./css/AdminAiUpload.css";

const functions = new Functions(client);

const ADMIN_USER_ID = process.env.REACT_APP_ADMIN_USER_ID;
const AI_UPLOAD_FUNCTION_ID = process.env.REACT_APP_GROQ_AI_FUNCTION_ID;

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = reader.result || "";
      const base64 = result.toString().split(",")[1];
      resolve(base64);
    };

    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function parseExecutionResponse(execution) {
  const raw =
    execution.responseBody || execution.response || execution.stdout || "";

  try {
    return JSON.parse(raw);
  } catch {
    return {
      success: false,
      detail: raw || "Function did not return JSON.",
      execution,
    };
  }
}

function formatDateTime(value) {
  if (!value) return "";

  return new Date(value).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function AdminAiUpload() {
  const { user } = useUser();

  const [pdfFile, setPdfFile] = useState(null);
  const [sourceTitle, setSourceTitle] = useState("");
  const [topic, setTopic] = useState("music");
  const [loading, setLoading] = useState(false);
  const [filesLoading, setFilesLoading] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState(null);
  const [indexedFiles, setIndexedFiles] = useState([]);
  const [log, setLog] = useState("");
  const [result, setResult] = useState(null);

  const isAdmin = user?.$id === ADMIN_USER_ID;

  const runFunction = async ({ path, method = "POST", body = {} }) => {
    if (!AI_UPLOAD_FUNCTION_ID) {
      throw new Error("Missing REACT_APP_GROQ_AI_FUNCTION_ID in .env");
    }

    const execution = await functions.createExecution(
      AI_UPLOAD_FUNCTION_ID,
      JSON.stringify(body),
      false,
      path,
      method,
      {
        "content-type": "application/json",
      },
    );

    const data = parseExecutionResponse(execution);

    if (!data.success) {
      throw new Error(data.detail || data.error || "Function failed.");
    }

    return data;
  };

  const fetchIndexedFiles = async () => {
    if (!isAdmin) return;

    setFilesLoading(true);

    try {
      const data = await runFunction({
        path: "/ai/admin/music/files",
        method: "GET",
      });

      setIndexedFiles(data.files || []);
    } catch (error) {
      console.error(error);
      setLog(error.message || "Could not load uploaded AI files.");
    } finally {
      setFilesLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchIndexedFiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const testAdminBackend = async () => {
    setLoading(true);
    setLog("Testing admin backend...");
    setResult(null);

    try {
      const data = await runFunction({
        path: "/ai/admin/test",
        method: "GET",
      });

      setResult(data);
      setLog("Admin backend verified.");
    } catch (error) {
      console.error(error);
      setLog(error.message || "Admin backend test failed.");
    } finally {
      setLoading(false);
    }
  };

  const uploadPdf = async () => {
    if (!pdfFile) {
      setLog("Please select a PDF first.");
      return;
    }

    if (!sourceTitle.trim()) {
      setLog("Please enter a source title.");
      return;
    }

    setLoading(true);
    setLog("Reading PDF...");
    setResult(null);

    try {
      const fileBase64 = await fileToBase64(pdfFile);

      setLog("Uploading PDF and creating Learn Music AI chunks...");

      const data = await runFunction({
        path: "/ai/admin/music/upload-pdf",
        method: "POST",
        body: {
          fileName: pdfFile.name,
          fileBase64,
          sourceTitle: sourceTitle.trim(),
          topic: topic.trim() || "music",
        },
      });

      setResult(data);
      setLog(`Uploaded successfully. Created ${data.chunksCreated} chunks.`);

      setPdfFile(null);
      setSourceTitle("");
      setTopic("music");

      await fetchIndexedFiles();
    } catch (error) {
      console.error(error);
      setLog(error.message || "PDF upload failed.");
    } finally {
      setLoading(false);
    }
  };

  const deleteAiFile = async (file) => {
    const confirmed = window.confirm(
      `Delete "${file.sourceTitle}"?\n\nThis will delete the PDF from HTECH Storage and all related music chunks.`,
    );

    if (!confirmed) return;

    setDeletingFileId(file.fileId);
    setLog(`Deleting ${file.sourceTitle}...`);
    setResult(null);

    try {
      const data = await runFunction({
        path: "/ai/admin/music/delete-pdf",
        method: "POST",
        body: {
          fileId: file.fileId,
        },
      });

      setResult(data);
      setLog(
        `Deleted "${file.sourceTitle}". Removed ${data.deletedChunks} chunks.`,
      );

      setIndexedFiles((prev) =>
        prev.filter((item) => item.fileId !== file.fileId),
      );
    } catch (error) {
      console.error(error);
      setLog(error.message || "Delete failed.");
    } finally {
      setDeletingFileId(null);
    }
  };

  if (!user) {
    return (
      <div className="admin-ai-upload-page">
        <div className="admin-ai-card">
          <h2>Admin AI Upload</h2>
          <p>Please login as admin.</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="admin-ai-upload-page">
        <div className="admin-ai-card">
          <h2>Access Denied</h2>
          <p>This page is only for the admin user ID.</p>
          <p className="admin-ai-small">Current user: {user.$id}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-ai-upload-page">
      <div className="admin-ai-card">
        <div className="admin-ai-header">
          <div>
            <h2>Admin AI Upload</h2>
            <p>
              Upload music PDFs. The Appwrite function stores the PDF in HTECH
              Storage and creates searchable chunks in HTECH Database.
            </p>
          </div>

          <button
            className="admin-ai-test-btn"
            onClick={testAdminBackend}
            disabled={loading || filesLoading}
          >
            Test Backend
          </button>
        </div>

        <div className="admin-ai-form">
          <label>
            Source Title
            <input
              type="text"
              value={sourceTitle}
              onChange={(e) => setSourceTitle(e.target.value)}
              placeholder="Example: EDM Mixing Handbook"
              disabled={loading}
            />
          </label>

          <label>
            Topic / Concept
            <input
              type="text"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="music, edm, mixing, mastering"
              disabled={loading}
            />
          </label>

          <label>
            PDF File
            <input
              type="file"
              accept="application/pdf,.pdf"
              disabled={loading}
              onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
            />
          </label>

          {pdfFile ? (
            <div className="admin-ai-file">
              Selected: {pdfFile.name} —{" "}
              {(pdfFile.size / 1024 / 1024).toFixed(2)} MB
            </div>
          ) : null}

          <button
            className="admin-ai-upload-btn"
            onClick={uploadPdf}
            disabled={loading || !pdfFile}
          >
            {loading ? (
              <>
                Uploading <ClipLoader size={14} color="#fff" />
              </>
            ) : (
              "Upload PDF to AI Knowledge Base"
            )}
          </button>
        </div>

        <section className="admin-ai-files-section">
          <div className="admin-ai-files-header">
            <div>
              <h3>Uploaded AI Knowledge Files</h3>
              <p>
                These are grouped from the chunks saved in{" "}
                <strong>musicKnowledgeChunks</strong>.
              </p>
            </div>

            <button
              className="admin-ai-refresh-btn"
              onClick={fetchIndexedFiles}
              disabled={filesLoading}
            >
              {filesLoading ? "Refreshing..." : "Refresh"}
            </button>
          </div>

          {filesLoading ? (
            <div className="admin-ai-loading-row">
              <ClipLoader size={16} color="#fff" />
              <span>Loading uploaded files...</span>
            </div>
          ) : indexedFiles.length === 0 ? (
            <div className="admin-ai-empty-files">
              No AI PDF files uploaded yet.
            </div>
          ) : (
            <div className="admin-ai-file-list">
              {indexedFiles.map((file) => (
                <div key={file.fileId} className="admin-ai-file-card">
                  <div className="admin-ai-file-main">
                    <h4>{file.sourceTitle}</h4>
                    <p>
                      Concept: <strong>{file.topic}</strong>
                    </p>
                    <p>Chunks: {file.chunkCount}</p>
                    <p>Updated: {formatDateTime(file.updatedAt)}</p>
                    <small>{file.fileId}</small>
                  </div>

                  <div className="admin-ai-file-actions">
                    {file.fileUrl ? (
                      <a
                        href={file.fileUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="admin-ai-view-link"
                      >
                        View PDF
                      </a>
                    ) : null}

                    <button
                      className="admin-ai-delete-btn"
                      onClick={() => deleteAiFile(file)}
                      disabled={deletingFileId === file.fileId}
                    >
                      {deletingFileId === file.fileId ? (
                        <ClipLoader size={12} color="#fff" />
                      ) : (
                        "Delete"
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {log ? <div className="admin-ai-log">{log}</div> : null}

        {result ? (
          <pre className="admin-ai-result">
            {JSON.stringify(result, null, 2)}
          </pre>
        ) : null}
      </div>
    </div>
  );
}

export default AdminAiUpload;
